import React from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Sparkles, X, ArrowRight, CheckCircle2 } from "lucide-react";

export default function UpgradeModal({ open, onClose, title, subtitle, reason = "quiz" }) {
  const navigate = useNavigate();
  if (!open) return null;

  const defaults = {
    quiz: {
      title: "Unlock the certification quizzes",
      subtitle:
        "Quizzes are part of any paid plan. Pass all 8 modules and earn your downloadable Platinum 1 Certified credential.",
    },
    module: {
      title: "This module is locked",
      subtitle:
        "Your free preview includes the first 2 modules. Upgrade to unlock the remaining 6 modules and all certification quizzes.",
    },
    soft: {
      title: "Enjoying the preview?",
      subtitle:
        "You're 25% of the way through your free preview. Upgrade now to unlock all 8 modules, all quizzes, and your Platinum 1 PDF certificate.",
    },
    certificate: {
      title: "Earn your downloadable certificate",
      subtitle:
        "The PDF certificate is included in every paid plan. Upgrade and download yours after passing all 8 quizzes.",
    },
  };
  const content = {
    title: title || defaults[reason].title,
    subtitle: subtitle || defaults[reason].subtitle,
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950/70 backdrop-blur-sm grid place-items-center px-4"
      data-testid="upgrade-modal"
    >
      <div className="relative w-full max-w-lg bg-white border border-slate-200 rounded-sm overflow-hidden shadow-2xl">
        <button
          onClick={onClose}
          data-testid="upgrade-modal-close"
          className="absolute top-3 right-3 w-8 h-8 grid place-items-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-sm"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="bg-slate-950 text-white p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-600 grid place-items-center rounded-sm">
              {reason === "soft" ? <Sparkles className="w-5 h-5 text-white" /> : <Lock className="w-5 h-5 text-white" />}
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-orange-400">
                {reason === "soft" ? "Keep going" : "Locked"}
              </div>
              <div className="font-display font-bold text-xl mt-0.5">{content.title}</div>
            </div>
          </div>
          <p className="mt-4 text-slate-300 text-sm leading-relaxed">{content.subtitle}</p>
        </div>
        <div className="p-6">
          <ul className="text-sm text-slate-800 space-y-2">
            {[
              "All 8 modules unlocked",
              "All certification quizzes",
              "Downloadable Platinum 1 PDF certificate",
              "3-day money-back guarantee",
            ].map((f) => (
              <li key={f} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-orange-600" /> {f}
              </li>
            ))}
          </ul>
          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => {
                onClose?.();
                navigate("/pricing");
              }}
              data-testid="upgrade-modal-cta"
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-sm font-semibold inline-flex items-center justify-center gap-2"
            >
              See plans <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              data-testid="upgrade-modal-dismiss"
              className="border border-slate-300 hover:bg-slate-50 text-slate-900 py-3 rounded-sm font-semibold px-5"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
